/**
 * Google Analytics 4 Event Names
 * Centralized constants for all tracking events
 */

// Pre-Authentication Events (Public Pages)
export const AUTH_EVENTS = {
  // Signup funnel
  SIGNUP_PAGE_VIEW: 'signup_page_view',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_METHOD_SELECTED: 'signup_method_selected',
  SIGNUP_SUBMITTED: 'signup_submitted',
  SIGNUP_SUCCESS: 'signup_success',
  SIGNUP_ERROR: 'signup_error',
  VERIFICATION_EMAIL_SENT: 'verification_email_sent',
  EMAIL_VERIFIED: 'email_verified',

  // Login funnel
  LOGIN_PAGE_VIEW: 'login_page_view',
  LOGIN_ATTEMPTED: 'login_attempted',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',

  // Password recovery
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',

  // Logout
  LOGOUT: 'logout',
} as const;

// Dashboard Events
export const DASHBOARD_EVENTS = {
  DASHBOARD_VIEW: 'dashboard_view',
  CHART_INTERACTION: 'chart_interaction',
  TILE_EXPANDED: 'tile_expanded',
  DASHBOARD_CUSTOMIZED: 'dashboard_customized',
  LAYOUT_SAVED: 'layout_saved',
  DATE_RANGE_CHANGED: 'date_range_changed',
  DATA_EXPORTED: 'data_exported',
  ARTIST_SWITCHED: 'artist_switched',
  REPORT_TOGGLED: 'report_toggled',
} as const;

// Nebula AI Events
export const NEBULA_EVENTS = {
  NEBULA_OPENED: 'nebula_opened',
  NEBULA_CHAT_STARTED: 'nebula_chat_started',
  NEBULA_MESSAGE_SENT: 'nebula_message_sent',
  NEBULA_VISUALIZATION_GENERATED: 'nebula_visualization_generated',
  NEBULA_CONVERSATION_LOADED: 'nebula_conversation_loaded',
  NEBULA_CONVERSATION_DELETED: 'nebula_conversation_deleted',
  NEBULA_NEW_CHAT: 'nebula_new_chat',
  NEBULA_EXPORT_PDF: 'nebula_export_pdf',
} as const;

// Banking Events
export const BANKING_EVENTS = {
  BANKING_VIEW: 'banking_view',
  BALANCE_VIEWED: 'balance_viewed',
  TRANSACTIONS_VIEWED: 'transactions_viewed',
} as const;

// Team Events
export const TEAM_EVENTS = {
  TEAM_VIEW: 'team_view',
  TEAM_CREATED: 'team_created',
  MEMBER_INVITED: 'member_invited',
  INVITATION_ACCEPTED: 'invitation_accepted',
  PERMISSIONS_CHANGED: 'permissions_changed',
  TEAM_DETAILS_VIEW: 'team_details_view',
} as const;

// Profile Events
export const PROFILE_EVENTS = {
  PROFILE_VIEW: 'profile_view',
  PROFILE_UPDATED: 'profile_updated',
  SETTINGS_CHANGED: 'settings_changed',
} as const;

// Admin Events
export const ADMIN_EVENTS = {
  ADMIN_PAGE_VIEW: 'admin_page_view',
  ADMIN_USER_ACTION: 'admin_user_action',
  ADMIN_ARTIST_MAPPING: 'admin_artist_mapping',
} as const;

// Error Events
export const ERROR_EVENTS = {
  API_ERROR: 'api_error',
  PAGE_ERROR: 'page_error',
} as const;

// Activation Milestone Events
export const ACTIVATION_EVENTS = {
  FIRST_CHART_INTERACTION: 'first_chart_interaction',
  FIRST_EXPORT: 'first_export',
  FIRST_INVITE: 'first_invite',
  FIRST_NEBULA_QUERY: 'first_nebula_query',
} as const;

// Page view event names by route
export const PAGE_VIEW_EVENTS = {
  // Public routes
  '/login': AUTH_EVENTS.LOGIN_PAGE_VIEW,
  '/signup': AUTH_EVENTS.SIGNUP_PAGE_VIEW,
  '/forgot-password': 'forgot_password_page_view',
  '/reset-password': 'reset_password_page_view',

  // Protected routes
  '/dashboard': DASHBOARD_EVENTS.DASHBOARD_VIEW,
  '/profile': PROFILE_EVENTS.PROFILE_VIEW,
  '/profile/account': PROFILE_EVENTS.PROFILE_VIEW,
  '/profile/change-password': PROFILE_EVENTS.PROFILE_VIEW,
  '/profile/artist': PROFILE_EVENTS.PROFILE_VIEW,
  '/settings': 'settings_page_view',
  '/nebula': NEBULA_EVENTS.NEBULA_OPENED,
  '/banking': BANKING_EVENTS.BANKING_VIEW,
  '/banking/activities': BANKING_EVENTS.TRANSACTIONS_VIEWED,
  '/teams': TEAM_EVENTS.TEAM_VIEW,
  '/teams/create': 'team_create_page_view',
  '/invitations': 'invitations_page_view',

  // Admin routes
  '/customer-details': ADMIN_EVENTS.ADMIN_PAGE_VIEW,
  '/users-list': ADMIN_EVENTS.ADMIN_PAGE_VIEW,
  '/artist-mapping': ADMIN_EVENTS.ADMIN_PAGE_VIEW,
  '/admin/roles': ADMIN_EVENTS.ADMIN_PAGE_VIEW,
  '/permissions': ADMIN_EVENTS.ADMIN_PAGE_VIEW,
} as const;

// Export all events for convenience
export const ANALYTICS_EVENTS = {
  ...AUTH_EVENTS,
  ...DASHBOARD_EVENTS,
  ...NEBULA_EVENTS,
  ...BANKING_EVENTS,
  ...TEAM_EVENTS,
  ...PROFILE_EVENTS,
  ...ADMIN_EVENTS,
  ...ERROR_EVENTS,
  ...ACTIVATION_EVENTS,
} as const;
